import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth/auth.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { XdsButtonComponent, XdsIconButtonComponent, XdsFormFieldComponent, XdsInputComponent } from '../../../../shared/ui';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.css'],
    imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, XdsButtonComponent, XdsIconButtonComponent, XdsFormFieldComponent, XdsInputComponent]
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() public invitation: string | null = null;
  @Input() public oldPassword: string | null = null;

  isOldPasswordHidden = false;
  showPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0;
  newPasswordForm: UntypedFormGroup;

  @Input() set email(value: string) {
    if (value) {
      if (this.oldPassword) {
        this.isOldPasswordHidden = true;
      }
      this.newPasswordForm = this.fb.group(
        {
          email: [value, Validators.required],
          oldPassword: [this.oldPassword, Validators.required],
          password: ['', [Validators.required, Validators.minLength(12), this.validatePasswordStrength.bind(this)]],
          confirmPassword: [''],
        },
        { validator: this.checkPasswords }
      );

      // Subscribe to password changes for strength indicator
      this.newPasswordForm.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.passwordStrength = this.calculatePasswordStrength(value);
      });
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private service: AuthService,
    public themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.newPasswordForm = this.fb.group(
      {
        email: [''],
        oldPassword: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(12), this.validatePasswordStrength.bind(this)]],
        confirmPassword: [''],
      },
      { validator: this.checkPasswords }
    );

    // Subscribe to password changes for strength indicator
    this.newPasswordForm.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.passwordStrength = this.calculatePasswordStrength(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkPasswords(group: UntypedFormGroup): any {
    const pass = group.get('password')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    return pass === confirmPass ? null : { notSame: true };
  }

  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;

    // Länge >= 12
    if (password.length >= 12) strength++;

    // Enthält Großbuchstaben
    if (/[A-Z]/.test(password)) strength++;

    // Enthält Kleinbuchstaben
    if (/[a-z]/.test(password)) strength++;

    // Enthält Ziffern
    if (/[0-9]/.test(password)) strength++;

    // Enthält Sonderzeichen
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    return strength;
  }

  getPasswordStrengthLabel(): string {
    switch (this.passwordStrength) {
      case 0:
      case 1: return 'REG_PW_LEVEL_WEAK';
      case 2: return 'REG_PW_LEVEL_MEDIUM';
      case 3: return 'REG_PW_LEVEL_GOOD';
      case 4: return 'REG_PW_LEVEL_STRONG';
      case 5: return 'REG_PW_LEVEL_VERY_STRONG';
      default: return 'REG_PW_LEVEL_WEAK';
    }
  }

  getPasswordStrengthData(): 'weak' | 'full' | null {
    if (this.passwordStrength <= 1) return 'weak';
    if (this.passwordStrength === 5) return 'full';
    return null;
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.newPasswordForm.get(controlName)?.hasError(errorName) || false;
  }

  validateControl(controlName: string): boolean {
    const control = this.newPasswordForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isConfirmPasswordInvalid(): boolean {
    const confirmPassword = this.newPasswordForm.get('confirmPassword');
    return (
      this.newPasswordForm.hasError('notSame') &&
      !!(confirmPassword?.touched || confirmPassword?.dirty)
    );
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  validatePasswordStrength(control: any): { [key: string]: boolean } | null {
    const value = control.value || '';
    const errors: { [key: string]: boolean } = {};

    if (value.length < 12) {
      errors['minlength'] = true;
    }
    if (!/[A-Z]/.test(value)) {
      errors['missingUpper'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['missingLower'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['missingDigit'] = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['missingSpecial'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.newPasswordForm.valid) {
      this.service.updateNewPassword(this.newPasswordForm.value);
    }
  }
}
